import React, { useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { Toaster } from './components/ui/sonner';
import { useAuthStore, useNotificationStore } from './store';
import { Layout } from './components/Layout';
import { AuthPage } from './components/AuthPage';
import { CEODashboard } from './components/CEODashboard';
import { MemberDashboard } from './components/MemberDashboard';
import { ClientPortal } from './components/ClientPortal';
import { CRMPage } from './components/CRMPage';
import { TeamPage } from './components/TeamPage';
import { TasksPage } from './components/TasksPage';
import { ObjectivesPage } from './components/ObjectivesPage';
import { MarketingPage } from './components/MarketingPage';
import { SalesPage } from './components/SalesPage';
import { OperationsPage } from './components/OperationsPage';
import { FinancePage } from './components/FinancePage';
import { ProductPage } from './components/ProductPage';
import { ServiceDeliveryPage } from './components/ServiceDeliveryPage';
import { LegalPage } from './components/LegalPage';
import { MindsetPage } from './components/MindsetPage';
import { IntegrationsPage } from './components/IntegrationsPage';
import { SocialMediaManager } from './components/SocialMediaManager';
import { ContentCalendarPage } from './components/ContentCalendarPage';
import { WorkJournalPage } from './components/WorkJournalPage';
import PublicationsPage from './components/PublicationsPage';
import ClientPortalPage from './components/ClientPortalPage';
import ClientWelcomePage from './components/ClientWelcomePage';
import SocialInboxPage from './components/SocialInboxPage';
import SetPasswordPage from './components/SetPasswordPage';
import ResetPasswordPage from './components/ResetPasswordPage';
import ProfilePage from './components/ProfilePage';
import LearningsPage from './components/LearningsPage';
import NotificationsPage from './components/NotificationsPage';
import EventsPage from './components/EventsPage';
import CeoCalendarPage from './components/CeoCalendarPage';
import CollaboratorPortal from './components/CollaboratorPortal';
import ResourcesPage from './components/ResourcesPage';
import AdHooksPage from './components/AdHooksPage';
import ReelsBankPage from './components/ReelsBankPage';
import TimeTrackingPage from './components/TimeTrackingPage';
import FeedbackPage from './components/FeedbackPage';
import UsersRolesPage from './components/UsersRolesPage';
import './App.css';

// Protected Route Component
function ProtectedRoute({ children, allowedRoles = [] }) {
  const { isAuthenticated, viewAsRole, user } = useAuthStore();
  
  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  const currentRole = viewAsRole || user?.role;
  const path = window.location.pathname;
  if (currentRole === 'collaborator' && !['/collab', '/notifications'].includes(path)) {
    return <Navigate to="/collab" replace />;
  }
  
  if (allowedRoles.length > 0 && !allowedRoles.includes(currentRole)) {
    // Redirect to appropriate dashboard based on role
    const roleRedirects = {
      ceo: '/ceo-dashboard',
      manager: '/dashboard',
      member: '/member-dashboard',
      client: '/client-portal',
      collaborator: '/collab',
    };
    return <Navigate to={roleRedirects[currentRole] || '/dashboard'} replace />;
  }

  return <Layout>{children}</Layout>;
}

// Placeholder pages for features not yet implemented
function PlaceholderPage({ title, description }) {
  return (
    <div className="flex flex-col items-center justify-center min-h-[60vh] text-center">
      <div className="w-16 h-16 rounded-2xl gradient-primary flex items-center justify-center mb-4">
        <span className="text-white text-2xl">🚧</span>
      </div>
      <h1 className="text-2xl font-bold mb-2">{title}</h1>
      <p className="text-muted-foreground max-w-md">{description}</p>
    </div>
  );
}

// Manager Dashboard (similar to CEO but with less data)
function ManagerDashboard() {
  return <CEODashboard />;
}

function App() {
  const { isAuthenticated } = useAuthStore();

  // Si la sesión cambia en otra pestaña (p.ej. entras al portal de un cliente),
  // recarga esta pestaña para no seguir operando con el token equivocado.
  useEffect(() => {
    const onStorage = (e) => {
      if (e.key !== 'karma-auth') return;
      try {
        const oldU = JSON.parse(e.oldValue || '{}')?.state?.user?.id || null;
        const newU = JSON.parse(e.newValue || '{}')?.state?.user?.id || null;
        if (oldU !== newU) window.location.reload();
      } catch { window.location.reload(); }
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, []);

  return (
    <BrowserRouter>
      <Toaster position="top-right" richColors />
      <Routes>
        {/* Public Routes */}
        <Route path="/login" element={<AuthPage />} />
        <Route path="/set-password" element={<SetPasswordPage />} />
        <Route path="/reset-password" element={<ResetPasswordPage />} />
        <Route path="/" element={<Navigate to="/login" replace />} />

        {/* CEO Routes */}
        <Route
          path="/ceo-dashboard"
          element={
            <ProtectedRoute allowedRoles={['ceo']}>
              <CEODashboard />
            </ProtectedRoute>
          }
        />
        <Route
          path="/ceo-calendar"
          element={
            <ProtectedRoute allowedRoles={['ceo', 'manager']}>
              <CeoCalendarPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/collab"
          element={
            <ProtectedRoute allowedRoles={['collaborator']}>
              <CollaboratorPortal />
            </ProtectedRoute>
          }
        />

        {/* Manager Routes */}
        <Route
          path="/dashboard"
          element={
            <ProtectedRoute allowedRoles={['ceo', 'manager']}>
              <ManagerDashboard />
            </ProtectedRoute>
          }
        />
        <Route
          path="/finance-dashboard"
          element={
            <ProtectedRoute allowedRoles={['ceo', 'manager']}>
              <FinancePage />
            </ProtectedRoute>
          }
        />

        {/* Member Routes */}
        <Route
          path="/member-dashboard"
          element={
            <ProtectedRoute allowedRoles={['ceo', 'manager', 'member']}>
              <MemberDashboard />
            </ProtectedRoute>
          }
        />

        {/* Client Routes */}
        <Route
          path="/client-portal"
          element={
            <ProtectedRoute allowedRoles={['ceo', 'client']}>
              <ClientPortalPage section="home" />
            </ProtectedRoute>
          }
        />
        <Route
          path="/client-portal/welcome"
          element={
            <ProtectedRoute allowedRoles={['client']}>
              <ClientWelcomePage />
            </ProtectedRoute>
          }
        />
        {['approvals', 'briefing', 'calendar', 'tasks', 'documents', 'invoices', 'reports', 'campaigns'].map((sec) => (
          <Route
            key={sec}
            path={`/client-portal/${sec}`}
            element={
              <ProtectedRoute allowedRoles={['ceo', 'client']}>
                <ClientPortalPage section={sec} />
              </ProtectedRoute>
            }
          />
        ))}
        {/* Proyectos se ha retirado del portal de cliente: el trabajo puntual se refleja en Campañas */}
        <Route path="/client-portal/projects" element={<Navigate to="/client-portal/campaigns" replace />} />


        {/* Shared Routes - All Roles */}
        <Route
          path="/tasks"
          element={
            <ProtectedRoute>
              <TasksPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/team"
          element={
            <ProtectedRoute>
              <TeamPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/team-directory"
          element={
            <ProtectedRoute allowedRoles={['ceo', 'manager']}>
              <TeamPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/crm"
          element={
            <ProtectedRoute allowedRoles={['ceo', 'manager', 'member']}>
              <CRMPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/projects"
          element={
            <ProtectedRoute>
              <PlaceholderPage 
                title="Proyectos" 
                description="Próximamente: Gestión de proyectos y seguimiento"
              />
            </ProtectedRoute>
          }
        />
        <Route
          path="/invoices"
          element={
            <ProtectedRoute>
              <PlaceholderPage 
                title="Facturas" 
                description="Próximamente: Gestión de facturas y pagos"
              />
            </ProtectedRoute>
          }
        />
        <Route
          path="/documents"
          element={
            <ProtectedRoute>
              <PlaceholderPage 
                title="Documentos" 
                description="Próximamente: Gestión de documentos compartidos"
              />
            </ProtectedRoute>
          }
        />
        <Route
          path="/reports"
          element={
            <ProtectedRoute>
              <PlaceholderPage 
                title="Informes" 
                description="Próximamente: Informes de rendimiento y métricas de tus proyectos"
              />
            </ProtectedRoute>
          }
        />
        <Route
          path="/campaigns"
          element={
            <ProtectedRoute>
              <PlaceholderPage 
                title="Campañas" 
                description="Próximamente: Estado y métricas de tus campañas de marketing"
              />
            </ProtectedRoute>
          }
        />

        {/* Dirección - CEO */}
        <Route
          path="/objectives"
          element={
            <ProtectedRoute allowedRoles={['ceo']}>
              <ObjectivesPage />
            </ProtectedRoute>
          }
        />

        {/* Portal de Equipo */}
        <Route
          path="/notifications"
          element={
            <ProtectedRoute>
              <NotificationsPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/events"
          element={
            <ProtectedRoute>
              <EventsPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/content-calendar"
          element={
            <ProtectedRoute allowedRoles={['ceo', 'manager', 'member']}>
              <ContentCalendarPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/resources"
          element={
            <ProtectedRoute>
              <ResourcesPage />
            </ProtectedRoute>
          }
        />
        
        {/* Integraciones - Nango */}
        <Route
          path="/resources/reels"
          element={
            <ProtectedRoute>
              <ReelsBankPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/resources/ganchos"
          element={
            <ProtectedRoute>
              <AdHooksPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/integrations"
          element={
            <ProtectedRoute allowedRoles={['ceo', 'manager']}>
              <IntegrationsPage />
            </ProtectedRoute>
          }
        />
        
        {/* Social Media Manager - Facebook/Instagram */}
        <Route
          path="/social-media"
          element={
            <ProtectedRoute allowedRoles={['ceo', 'manager']}>
              <SocialMediaManager />
            </ProtectedRoute>
          }
        />
        <Route
          path="/time-off"
          element={
            <ProtectedRoute allowedRoles={['ceo', 'manager', 'member']}>
              <TeamPage defaultTab="vacaciones" />
            </ProtectedRoute>
          }
        />
        <Route
          path="/time-tracking"
          element={
            <ProtectedRoute allowedRoles={['ceo', 'manager']}>
              <TimeTrackingPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/forum"
          element={
            <ProtectedRoute>
              <PlaceholderPage 
                title="Foro" 
                description="Próximamente: Discusiones y Q&A del equipo"
              />
            </ProtectedRoute>
          }
        />
        <Route
          path="/recognition"
          element={
            <ProtectedRoute>
              <PlaceholderPage 
                title="Reconocimientos" 
                description="Próximamente: Celebra los logros del equipo"
              />
            </ProtectedRoute>
          }
        />
        <Route
          path="/feedback"
          element={
            <ProtectedRoute allowedRoles={['ceo', 'manager', 'member']}>
              <FeedbackPage />
            </ProtectedRoute>
          }
        />

        {/* Journal - Diario de trabajo personal (equipo) */}
        <Route
          path="/journal"
          element={
            <ProtectedRoute allowedRoles={['ceo', 'manager', 'member']}>
              <WorkJournalPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/actions"
          element={
            <ProtectedRoute allowedRoles={['ceo']}>
              <PlaceholderPage 
                title="Acciones" 
                description="Próximamente: Objetivos, hábitos, actividades y skills"
              />
            </ProtectedRoute>
          }
        />
        <Route
          path="/identity"
          element={
            <ProtectedRoute allowedRoles={['ceo']}>
              <PlaceholderPage 
                title="Identidad" 
                description="Próximamente: Perfil generado por IA con misión, visión y valores"
              />
            </ProtectedRoute>
          }
        />
        <Route
          path="/health"
          element={
            <ProtectedRoute allowedRoles={['ceo']}>
              <PlaceholderPage 
                title="Salud" 
                description="Próximamente: Dashboard de salud con métricas y analíticas"
              />
            </ProtectedRoute>
          }
        />
        <Route
          path="/knowledge"
          element={
            <ProtectedRoute allowedRoles={['ceo']}>
              <PlaceholderPage 
                title="Conocimiento" 
                description="Próximamente: Cursos, libros, mentores y eventos"
              />
            </ProtectedRoute>
          }
        />

        {/* Departamentos */}
        <Route
          path="/marketing"
          element={
            <ProtectedRoute allowedRoles={['ceo', 'manager']}>
              <MarketingPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/sales"
          element={
            <ProtectedRoute allowedRoles={['ceo', 'manager']}>
              <SalesPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/operations"
          element={
            <ProtectedRoute allowedRoles={['ceo', 'manager']}>
              <OperationsPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/service-delivery"
          element={
            <ProtectedRoute allowedRoles={['ceo', 'manager', 'member']}>
              <ServiceDeliveryPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/learnings"
          element={
            <ProtectedRoute allowedRoles={['ceo']}>
              <LearningsPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/publications"
          element={
            <ProtectedRoute allowedRoles={['ceo', 'manager', 'member']}>
              <PublicationsPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/social-inbox"
          element={
            <ProtectedRoute allowedRoles={['ceo', 'manager', 'member']}>
              <SocialInboxPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/product"
          element={
            <ProtectedRoute allowedRoles={['ceo', 'manager']}>
              <ProductPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/legal"
          element={
            <ProtectedRoute allowedRoles={['ceo']}>
              <LegalPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/mindset"
          element={
            <ProtectedRoute allowedRoles={['ceo']}>
              <MindsetPage />
            </ProtectedRoute>
          }
        />

        {/* Settings */}
        <Route
          path="/profile"
          element={
            <ProtectedRoute>
              <ProfilePage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/settings"
          element={
            <ProtectedRoute allowedRoles={['ceo', 'manager']}>
              <PlaceholderPage 
                title="Configuración" 
                description="Próximamente: Configuración general del sistema"
              />
            </ProtectedRoute>
          }
        />
        <Route
          path="/settings/users"
          element={
            <ProtectedRoute allowedRoles={['ceo']}>
              <UsersRolesPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/settings/integrations"
          element={
            <ProtectedRoute allowedRoles={['ceo']}>
              <PlaceholderPage 
                title="Integraciones" 
                description="Próximamente: Configuración de Meta Ads, Apple Health y más"
              />
            </ProtectedRoute>
          }
        />

        {/* Catch all - redirect to login */}
        <Route path="*" element={<Navigate to="/login" replace />} />
      </Routes>
    </BrowserRouter>
  );
}

export default App;
